import React, { MouseEventHandler } from "react";
import { useTranslation } from 'react-i18next';
import { Destination, DestinationType, Organization } from "../common/ObjectTypes";
import ConnectionDestinationTable from "../destinations/ConnectionDestinationTable";
import GlobalSearch from "../common/GlobalSearch";
import SelectInput from "../common/SelectInput";
import { filterClasses } from "../common/tailwindClassnames";

type FilterOption = { label: string; value: string };

type ConnectionDetailsDestinationProps = {
    destinations: Destination[],
    existingDestPhase0: Destination[],
    existingDestPhase1: Destination[],
    existingDestPhase2: Destination[],
    existingDestPhase3: Destination[],
    onSelectDestination: Function,
    onRemoveDestination: Function,
    onViewDetails: Function,
    onOpenRules?: (destination: Destination) => void,
    destinationsWithRules?: Set<string>,
    organizations: Organization[],
    loadingOrganizations: boolean,
    destinationTypes: DestinationType[],
    loadingDestinationTypes: boolean,
    onClickAddDestination: Function,
    onSmartAddDestination: Function,
    hasAvailableDestinations?: boolean | null,
    onAddExistingDestinations: MouseEventHandler,
    onAddNewDestination?: (dest: Destination) => void,
    updatingDestinations: boolean,
    addExistingDest: boolean,
    globalSearchStr: string,
    onChangeGlobalDestinationSearch: (value: string) => void,
    typeFilterValue: FilterOption | null,
    onChangeTypeFilter: Function,
    ownerFilterValue: FilterOption | null,
    onChangeOwnerFilter: Function,
    searchTerm?: string
}

const ConnectionDetailsDestination = ({
    destinations,
    existingDestPhase0,
    existingDestPhase1,
    existingDestPhase2,
    existingDestPhase3,
    onSelectDestination,
    onRemoveDestination,
    onViewDetails,
    onOpenRules,
    destinationsWithRules,
    organizations,
    loadingOrganizations,
    destinationTypes,
    loadingDestinationTypes,
    onClickAddDestination,
    onSmartAddDestination,
    hasAvailableDestinations = null,
    onAddExistingDestinations,
    onAddNewDestination,
    updatingDestinations,
    addExistingDest,
    globalSearchStr,
    onChangeGlobalDestinationSearch,
    typeFilterValue,
    onChangeTypeFilter,
    ownerFilterValue,
    onChangeOwnerFilter,
    searchTerm = "",
}: ConnectionDetailsDestinationProps) => {
    const { t } = useTranslation();
    return (
        <div className="mt-8 min-h-[calc(100vh-280px)]">
            <div className="flex flex-row justify-between items-center w-full">
                <label className="text-secondary-gray">
                    {destinations.length === 0
                        ? (hasAvailableDestinations === false
                            ? t("This connection needs a destination.")
                            : t("This connection needs a destination. Select one from the list below."))
                        : t("Data from the provider are sent to the following destinations.")}
                </label>
            </div>
            <div className="flex flex-col mt-8 mb-12">
                {addExistingDest && <>
                    {/* Horizontal filter bar */}
                    <div className="flex flex-row gap-3 mb-3 flex-wrap">
                        <div className="flex flex-col gap-1">
                            <span className={filterClasses.label}>{t("Search")}</span>
                            <GlobalSearch onChangeGlobalSearch={onChangeGlobalDestinationSearch} savedString={globalSearchStr} />
                        </div>
                        <div className="flex flex-col gap-1">
                            <span className={filterClasses.label}>{t("Technology")}</span>
                            <SelectInput
                                name="type__in"
                                options={destinationTypes.map(d => ({ label: d.name, value: d.id }))}
                                value={typeFilterValue}
                                onChange={onChangeTypeFilter}
                                isMulti={false}
                                isClearable={true}
                                isSearchable={true}
                                isDisabled={false}
                                isLoading={loadingDestinationTypes}
                                className="w-48"
                                variant="connectionFilter"
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <span className={filterClasses.label}>{t("Workspace")}</span>
                            <SelectInput
                                name="owner__in"
                                options={organizations.map(org => ({ label: org.name, value: org.id }))}
                                value={ownerFilterValue}
                                onChange={onChangeOwnerFilter}
                                isMulti={false}
                                isClearable={true}
                                isSearchable={true}
                                isDisabled={false}
                                isLoading={loadingOrganizations}
                                className="w-48"
                                variant="connectionFilter"
                            />
                        </div>
                    </div>
                </>}
                <ConnectionDestinationTable
                    newConnection={false}
                    destinations={destinations}
                    existingDestPhase0={existingDestPhase0}
                    existingDestPhase1={existingDestPhase1}
                    existingDestPhase2={existingDestPhase2}
                    existingDestPhase3={existingDestPhase3}
                    addExistingDestinations={addExistingDest}
                    onAddExistingDestinations={onAddExistingDestinations}
                    updatingDestinations={updatingDestinations}
                    onClickAddDestination={onClickAddDestination}
                    onSmartAddDestination={onSmartAddDestination}
                    hasAvailableDestinations={hasAvailableDestinations}
                    onViewDetails={onViewDetails}
                    onRemoveDestination={onRemoveDestination}
                    onSelectDestination={onSelectDestination}
                    onOpenRules={onOpenRules}
                    destinationsWithRules={destinationsWithRules}
                    searchTerm={searchTerm}
                />
            </div>
        </div>
    )
};

export default ConnectionDetailsDestination;
